/**
 * Portraits Service
 *
 * GET /portraits/students — the job's students with their portraits. Envelope:
 *   { result, payload: PortraitStudent[] }
 * `searchName` is fuzzy-matched and ranked by the load service (`portrait_name_search.go`: exact
 * substring first, then one edit — two from eight characters — best match first), so the rows
 * come back already in the order the tray shows them. `gradeId` is applied upstream and combines
 * with the name search (AND), and so do `homeroomId` and `customId` (a custom category's id), each
 * independent of the others: all AND. `placed=any` makes the backend add `studentUsedOnPage` to
 * every row (true when any of the student's portraits is on a page, following Core's indexing,
 * which can lag editor changes; `usedOnPage` stays per portrait). Without `placed` the rows carry
 * no student-level flag. The endpoint also takes `yes` / `no` to filter on the server; the tray
 * sends `any` and applies Yes / No itself. With no params the whole roster returns.
 *
 * GET /portraits/grades — the job's grades. Envelope:
 *   { result, payload: [{ gradeId, gradeName, studentCount }] }
 * GET /portraits/homerooms — the job's homerooms. Envelope:
 *   { result, payload: [{ homeroomId, homeroomName, studentCount }] }
 * GET /portraits/custom-categories — the job's custom groups (clubs, teams, programs). Names are
 * not unique, so the id is what the students filter takes. Envelope:
 *   { result, payload: [{ customCategoryId, customCategoryName, studentCount }] }
 *
 * GET /portrait/:portraitId — one portrait, which the Photos panel reads for the pages a placed
 * portrait is on (`portraitPageUsageList`, a list of `{ pageNumber }`). The backend proxies Core's
 * body untouched. Envelope:
 *   { result, payload: PortraitDetail }
 */

import { httpClient } from '@/api/http/client'
import type {
  PortraitCustomCategory,
  PortraitDetail,
  PortraitDetailResponse,
  PortraitGrade,
  PortraitHomeroom,
  PortraitListResponse,
  PortraitStudent,
} from '@/api/types/portrait'

export interface PortraitStudentSearchParams {
  searchName?: string
  gradeId?: string
  homeroomId?: string
  customId?: string
  /** `any` adds each student's placed flag to every row. Omit for rows without the flag. */
  placed?: 'any'
}

/** GET a `{ result, payload: T[] }` envelope, failing with the upstream message (or
 *  `failureMessage`) when it reports a failure or the payload is not a list. */
async function getList<T>(path: string, failureMessage: string): Promise<T[]> {
  const response = await httpClient.get<PortraitListResponse<T>>(path)

  if (!response.result || !Array.isArray(response.payload)) {
    throw new Error(response.basicMessage || failureMessage)
  }

  return response.payload
}

async function searchStudents(
  params: PortraitStudentSearchParams = {}
): Promise<PortraitStudent[]> {
  const query = new URLSearchParams()
  const searchName = params.searchName?.trim()
  const gradeId = params.gradeId?.trim()
  const homeroomId = params.homeroomId?.trim()
  const customId = params.customId?.trim()

  if (searchName) {
    query.set('searchName', searchName)
  }

  if (gradeId) {
    query.set('gradeId', gradeId)
  }

  if (homeroomId) {
    query.set('homeroomId', homeroomId)
  }

  if (customId) {
    query.set('customId', customId)
  }

  if (params.placed) {
    query.set('placed', params.placed)
  }

  const qs = query.toString()

  return getList<PortraitStudent>(
    qs ? `/portraits/students?${qs}` : '/portraits/students',
    'Failed to load portraits'
  )
}

function getGrades(): Promise<PortraitGrade[]> {
  return getList<PortraitGrade>('/portraits/grades', 'Failed to load grades')
}

function getHomerooms(): Promise<PortraitHomeroom[]> {
  return getList<PortraitHomeroom>('/portraits/homerooms', 'Failed to load homerooms')
}

function getCustomCategories(): Promise<PortraitCustomCategory[]> {
  return getList<PortraitCustomCategory>(
    '/portraits/custom-categories',
    'Failed to load custom groups'
  )
}

/** GET /portrait/:portraitId — one portrait with the pages it is placed on
 *  (`portraitPageUsageList`). Envelope: { result, payload: PortraitDetail } */
async function getPortraitDetail(portraitId: string): Promise<PortraitDetail> {
  const { result, payload, basicMessage } = await httpClient.get<PortraitDetailResponse>(
    `/portrait/${encodeURIComponent(portraitId)}`
  )

  if (!result || !payload) {
    throw new Error(basicMessage || `Failed to load portrait ${portraitId}`)
  }

  return payload
}

export const portraitsService = {
  searchStudents,
  getGrades,
  getHomerooms,
  getCustomCategories,
  getPortraitDetail,
}
